import React from "react";
import {
  ArrowDownWideNarrow,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { MdOutlineDirectionsCar } from "react-icons/md";
import VehicleItem from "./VehicleItem";

const PAGE_SIZE = 6;
const ROOT_GROUP_NAME = "Lewootrack";
const MAX_VISIBLE_GROUPS = 9;
const ESTIMATED_GROUP_CARD_HEIGHT = 46;
const ESTIMATED_ROOT_GROUP_HEIGHT = 44;

const GROUP_COLORS = [
  "bg-[#16a34a]",
  "bg-[#0284c7]",
  "bg-[#2563eb]",
  "bg-[#ef4444]",
  "bg-[#64748b]",
  "bg-[#f59e0b]",
  "bg-[#475569]",
  "bg-[#ec4899]",
  "bg-[#0d9488]",
];

const VehicleList = ({
  vehicles: vehiclesList,
  selectedVehicleIds = [],
  selectedVehicle,
  onSelectedVehicleChange,
  onVisibleVehiclesChange,
  designVariant = "default",
  showVehicleActions = true,
}) => {
  const [searchValue, setSearchValue] = React.useState("");
  const [selectedTab, setSelectedTab] = React.useState("all");
  const [groupedView, setGroupedView] = React.useState(true);
  const [lineView, setLineView] = React.useState(true);
  const [sortOrder, setSortOrder] = React.useState("desc");
  const [expandedGroups, setExpandedGroups] = React.useState({});
  const [expandedVehicleDetails, setExpandedVehicleDetails] = React.useState({});
  const [page, setPage] = React.useState(1);
  const [panelMode, setPanelMode] = React.useState("groupes");
  const tabsScrollRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const autoSelectionAttemptRef = React.useRef("");
  const autoExpandedVehicleRef = React.useRef("");
  const navigate = useNavigate();
  const location = useLocation();
  const isGroupsPanelDesign = designVariant === "groups-panel";
  const normalizedPathname = String(location.pathname || "").toLowerCase();
  const isAutoSelectableV2Route =
    normalizedPathname.includes("/event-center-v2") ||
    normalizedPathname.includes("/dashcam-v2");

  const normalizeDeviceToken = React.useCallback((value) => {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/^it_/i, "");
  }, []);

  const getGroupLabel = React.useCallback((vehicle) => {
    const clientName = String(vehicle?.clientName || "").trim();
    if (clientName) {
      return clientName;
    }

    if (Array.isArray(vehicle?.vehicleGroups) && vehicle.vehicleGroups.length > 0) {
      const first = String(vehicle.vehicleGroups[0] ?? "").trim();
      return first || "SANS GROUPE";
    }

    if (typeof vehicle?.vehicleGroups === "string") {
      const normalized = vehicle.vehicleGroups.trim();
      return normalized || "SANS GROUPE";
    }

    return "SANS GROUPE";
  }, []);

  let vehicles = Object.values(vehiclesList ?? {});
  vehicles.sort((a, b) =>
    sortOrder === "desc"
      ? (b.timestamp ?? 0) - (a.timestamp ?? 0)
      : (a.timestamp ?? 0) - (b.timestamp ?? 0)
  );

  if (searchValue.trim()) {
    const needle = searchValue.trim().toLowerCase();
    vehicles = vehicles.filter((vehicle) => {
      const vehicleNumber = String(vehicle?.vehicleNumber ?? "").toLowerCase();
      const groupName = String(getGroupLabel(vehicle)).toLowerCase();
      return vehicleNumber.includes(needle) || groupName.includes(needle);
    });
  }

  if (
    !isGroupsPanelDesign &&
    (selectedTab === "online" || selectedTab === "idle" || selectedTab === "arrete")
  ) {
    vehicles = vehicles.filter((vehicle) =>
      String(vehicle.status || "").toLowerCase().includes(selectedTab)
    );
  }

  const baseVehicles = Object.values(vehiclesList ?? {});
  const counts = {
    all: baseVehicles.length,
  };

  const tabs = [
    { key: "all", label: "Tracking info", tone: "text-slate-700", bg: "bg-slate-100" },
    { key: "current-drivers", label: "Current Drivers", tone: "text-blue-600", bg: "bg-slate-100" },
    { key: "current-parking", label: "Current Parking", tone: "text-green-600", bg: "bg-slate-100" },
    { key: "summary", label: "Summary", tone: "text-orange-500", bg: "bg-slate-100" },
  ];

  const totalPages = Math.max(1, Math.ceil(vehicles.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedVehicles = vehicles.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const groupedVehicles = React.useMemo(() => {
    const sourceVehicles = isGroupsPanelDesign ? vehicles : paginatedVehicles;
    const buckets = sourceVehicles.reduce((acc, vehicle) => {
      const label = getGroupLabel(vehicle);
      if (!acc[label]) acc[label] = [];
      acc[label].push(vehicle);
      return acc;
    }, {});

    return Object.entries(buckets).sort((a, b) => {
      if (b[1].length !== a[1].length) {
        return b[1].length - a[1].length;
      }
      return a[0].localeCompare(b[0]);
    });
  }, [getGroupLabel, isGroupsPanelDesign, paginatedVehicles, vehicles]);
  const hasClientGrouping = React.useMemo(
    () => vehicles.some((vehicle) => String(vehicle?.clientName || "").trim()),
    [vehicles]
  );

  const isVehicleSelected = (vehicle) =>
    selectedVehicle?.uniqueId === vehicle.uniqueId ||
    selectedVehicleIds.includes(vehicle.id) ||
    selectedVehicleIds.includes(vehicle.uniqueId);

  const expandGroupForVehicle = React.useCallback(
    (vehicle) => {
      if (!isGroupsPanelDesign || !vehicle) return;

      const groupName = getGroupLabel(vehicle);
      if (!groupName) return;

      setPanelMode("groupes");
      setExpandedGroups((prev) => ({
        ...prev,
        [groupName]: true,
      }));
    },
    [getGroupLabel, isGroupsPanelDesign]
  );

  const handleCheckboxToggle = (vehicle) => {
    if (isVehicleSelected(vehicle)) {
      onSelectedVehicleChange?.(null);
      return;
    }
    onSelectedVehicleChange?.(vehicle);
  };

  const getVehicleKey = (vehicle, fallbackKey) =>
    String(vehicle?.uniqueId ?? vehicle?.id ?? vehicle?.vehicleNumber ?? fallbackKey);

  const toggleVehicleDetails = (vehicle, fallbackKey) => {
    const key = getVehicleKey(vehicle, fallbackKey);
    setExpandedVehicleDetails((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  React.useEffect(() => {
    setPage(1);
  }, [searchValue, selectedTab, panelMode, location.search]);

  React.useEffect(() => {
    if (!groupedVehicles.length) return;

    setExpandedGroups((prev) => {
      const next = {};
      groupedVehicles.forEach(([groupName]) => {
        next[groupName] = prev[groupName] ?? !isGroupsPanelDesign;
      });
      return next;
    });
  }, [groupedVehicles, isGroupsPanelDesign]);

  React.useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  React.useEffect(() => {
    if (!isAutoSelectableV2Route) return;

    const params = new URLSearchParams(location.search || "");
    const requestedDevice = params.get("device");
    const requestKey = `${location.pathname}|${location.search}`;

    if (!requestedDevice) {
      autoSelectionAttemptRef.current = "";
      return;
    }

    if (autoSelectionAttemptRef.current === requestKey) {
      return;
    }

    if (!vehicles.length) {
      return;
    }

    const requestedToken = normalizeDeviceToken(requestedDevice);
    const matchedVehicle = vehicles.find((vehicle) => {
      const candidates = [
        vehicle?.uniqueId,
        vehicle?.id,
        vehicle?.vehicleNumber,
        vehicle?.plate,
        vehicle?.imei,
      ];

      return candidates.some(
        (candidate) => normalizeDeviceToken(candidate) === requestedToken
      );
    });

    autoSelectionAttemptRef.current = requestKey;

    if (!matchedVehicle) {
      return;
    }

    expandGroupForVehicle(matchedVehicle);

    if (isVehicleSelected(matchedVehicle)) {
      return;
    }

    onSelectedVehicleChange?.(matchedVehicle);
  }, [
    isAutoSelectableV2Route,
    expandGroupForVehicle,
    isVehicleSelected,
    location.pathname,
    location.search,
    normalizeDeviceToken,
    onSelectedVehicleChange,
    vehicles,
  ]);

  React.useEffect(() => {
    if (!isGroupsPanelDesign || !selectedVehicle?.uniqueId) {
      autoExpandedVehicleRef.current = "";
      return;
    }

    if (autoExpandedVehicleRef.current === selectedVehicle.uniqueId) {
      return;
    }

    const matchedVehicle =
      vehicles.find((vehicle) => vehicle?.uniqueId === selectedVehicle.uniqueId) ||
      selectedVehicle;

    expandGroupForVehicle(matchedVehicle);
    autoExpandedVehicleRef.current = selectedVehicle.uniqueId;
  }, [expandGroupForVehicle, isGroupsPanelDesign, selectedVehicle, vehicles]);

  React.useEffect(() => {
    const syncVisibleVehicles = () => {
      if (!containerRef.current || containerRef.current.offsetParent === null) {
        return;
      }

      let visibleVehicles = paginatedVehicles;

      if (isGroupsPanelDesign) {
        if (panelMode === "groupes") {
          visibleVehicles = vehicles;
        } else {
          visibleVehicles = vehicles;
        }
      } else {
        visibleVehicles = groupedView
          ? groupedVehicles.flatMap(([groupName, groupVehicles]) =>
              expandedGroups[groupName] === false ? [] : groupVehicles
            )
          : paginatedVehicles;
      }

      onVisibleVehiclesChange?.(visibleVehicles);
    };

    syncVisibleVehicles();
    window.addEventListener("resize", syncVisibleVehicles);

    return () => {
      window.removeEventListener("resize", syncVisibleVehicles);
    };
  }, [
    onVisibleVehiclesChange,
    paginatedVehicles,
    groupedView,
    groupedVehicles,
    expandedGroups,
    isGroupsPanelDesign,
    panelMode,
    vehicles,
  ]);

  const scrollTabs = (direction) => {
    if (!tabsScrollRef.current) return;
    tabsScrollRef.current.scrollBy({
      left: direction * 140,
      behavior: "smooth",
    });
  };

  if (isGroupsPanelDesign) {
    const shouldScrollGroups = groupedVehicles.length > MAX_VISIBLE_GROUPS;
    const groupScrollMaxHeight =
      MAX_VISIBLE_GROUPS * ESTIMATED_GROUP_CARD_HEIGHT +
      (hasClientGrouping ? ESTIMATED_ROOT_GROUP_HEIGHT : 0);

    const renderGroupCards = () => (
      <div
        className={`space-y-1 ${
          shouldScrollGroups
            ? "overflow-y-scroll pr-1 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#cbd5e1] [&::-webkit-scrollbar-track]:bg-transparent"
            : ""
        }`}
        style={shouldScrollGroups ? { maxHeight: `${groupScrollMaxHeight}px` } : undefined}
      >
        {hasClientGrouping ? (
          <div className="rounded-lg border border-[#0f766e]/20 bg-gradient-to-r from-[#0f766e] to-[#14b8a6] px-2 py-1.5 text-white shadow-[0_3px_8px_rgba(15,118,110,0.16)]">
            <p className="text-[13px] font-bold uppercase leading-none">{ROOT_GROUP_NAME}</p>
            <p className="mt-0.5 text-[10px] font-medium text-white/90">
              {counts.all} vehicules
            </p>
          </div>
        ) : null}
        {groupedVehicles.map(([groupName, groupVehicles], index) => {
          const isExpanded = expandedGroups[groupName] === true;
          const colorClass = GROUP_COLORS[index % GROUP_COLORS.length];

          return (
            <div key={groupName} className="rounded-lg border border-[#dfe5ec] bg-white px-2 py-1 shadow-[0_2px_6px_rgba(15,23,42,0.04)]">
              <button
                type="button"
                onClick={() =>
                  setExpandedGroups((prev) => ({
                    ...prev,
                    [groupName]: !isExpanded,
                  }))
                }
                className="flex w-full items-center justify-between gap-2 text-left"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${colorClass}`} />
                    <span className="truncate text-[12px] font-semibold uppercase leading-none text-[#1f2937]">
                      {groupName}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[10px] font-medium text-[#6b7280]">
                    {groupVehicles.length} vehicules
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-[#eef2f6] px-1 text-[9px] font-semibold text-[#374151]">
                    {groupVehicles.length}
                  </span>
                  <ChevronDown
                    className={`h-3.5 w-3.5 text-[#94a3b8] transition-transform ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </button>

              {isExpanded ? (
                <div className="mt-1.5 space-y-1 border-t border-[#eef2f7] pt-1.5">
                  {groupVehicles.map((vehicle, vehicleIndex) => {
                    const fallbackKey = `${groupName}-${vehicleIndex}`;
                    return (
                      <div
                        key={vehicle.id ?? vehicle.uniqueId ?? fallbackKey}
                        className={`rounded-2xl transition ${
                          isVehicleSelected(vehicle) ? "ring-2 ring-[#0ea5e9]" : ""
                        }`}
                        onClick={() => onSelectedVehicleChange?.(vehicle)}
                      >
                        <VehicleItem
                          vehicle={vehicle}
                          navigate={navigate}
                          compact={true}
                          checked={isVehicleSelected(vehicle)}
                          onCheckboxChange={() => handleCheckboxToggle(vehicle)}
                          showActionButtons={showVehicleActions}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );

    const renderVehiclesList = () => (
      <div className="space-y-2">
        {vehicles.map((vehicle, index) => {
          const detailKey = getVehicleKey(vehicle, index);
          const detailOpen = Boolean(expandedVehicleDetails[detailKey]);

          return (
            <div
              key={vehicle.id ?? vehicle.uniqueId ?? index}
              className={`space-y-1 rounded-2xl transition ${
                isVehicleSelected(vehicle) ? "ring-2 ring-[#0ea5e9]" : ""
              }`}
              onClick={() => onSelectedVehicleChange?.(vehicle)}
            >
              <VehicleItem
                vehicle={vehicle}
                navigate={navigate}
                compact={true}
                showDetailsToggle={true}
                detailsOpen={detailOpen}
                onToggleDetails={() => toggleVehicleDetails(vehicle, index)}
                checked={isVehicleSelected(vehicle)}
                onCheckboxChange={() => handleCheckboxToggle(vehicle)}
                showActionButtons={showVehicleActions}
              />
              {detailOpen ? (
                <div className="pl-1.5">
                  <VehicleItem
                    vehicle={vehicle}
                    navigate={navigate}
                    compact={false}
                    checked={isVehicleSelected(vehicle)}
                    onCheckboxChange={() => handleCheckboxToggle(vehicle)}
                    showActionButtons={showVehicleActions}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );

    return (
      <div ref={containerRef} className="h-full rounded-2xl border border-[#dbe3eb] bg-[#f8fafc]">
        <div className="flex h-full flex-col">
          <div className="border-b border-[#e2e8f0] px-4 pb-2.5 pt-3.5">
            <div className="mb-3.5 flex items-center gap-2">
              <h3 className="text-[22px] font-extrabold leading-none text-[#111827]">Vehicules</h3>
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#eef2f6] px-1.5 text-[11px] font-bold text-[#475569]">
                {counts.all}
              </span>
            </div>

            <div className="mb-3.5 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPanelMode("tous")}
                className={`rounded-full px-3 py-[5px] text-[13px] font-semibold transition ${
                  panelMode === "tous"
                    ? "bg-[#eaf5f5] text-[#0f766e]"
                    : "text-[#94a3b8] hover:text-[#475569]"
                }`}
              >
                Tous
              </button>
              <button
                type="button"
                onClick={() => setPanelMode("ligne")}
                className={`rounded-full px-3 py-[5px] text-[13px] font-semibold transition ${
                  panelMode === "ligne"
                    ? "bg-[#eaf5f5] text-[#0f766e]"
                    : "text-[#94a3b8] hover:text-[#475569]"
                }`}
              >
                Ligne
              </button>
              <button
                type="button"
                onClick={() => setPanelMode("groupes")}
                className={`rounded-full px-3 py-[5px] text-[13px] font-semibold transition ${
                  panelMode === "groupes"
                    ? "border-b-2 border-[#0ea5a4] bg-[#eaf5f5] text-[#0f766e]"
                    : "text-[#64748b] hover:text-[#334155]"
                }`}
              >
                Groupes
              </button>
              <button
                type="button"
                className="ml-auto inline-flex h-[30px] w-[30px] items-center justify-center rounded-full border border-[#d2dbe6] bg-white text-[#64748b]"
                title="Ajouter un groupe"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="flex items-center gap-2 rounded-xl border border-[#d6deea] bg-white px-2.5 py-[5px]">
              <Search className="h-3.5 w-3.5 text-[#94a3b8]" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Rechercher un groupe"
                className="w-full border-0 bg-transparent text-[11px] text-[#334155] outline-none placeholder:text-[#94a3b8]"
              />
              <button
                type="button"
                title="Filtrer"
                className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-md text-[#64748b] hover:bg-[#f1f5f9]"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3">
            {panelMode === "groupes" ? renderGroupCards() : renderVehiclesList()}
          </div>

        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-full rounded-2xl border border-slate-200 bg-[#fafbfc] shadow-[0_14px_40px_rgba(37,99,235,0.10)] backdrop-blur-sm"
    >
      <div className="flex h-full flex-col">
        <div className="border-b border-blue-100 px-4 pb-3 pt-2">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-slate-900">
              Vehicle List
            </h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setLineView((prev) => !prev)}
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
                  lineView
                    ? "bg-blue-100 text-blue-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                Ligne
              </button>
              <button
                type="button"
                onClick={() => setGroupedView((prev) => !prev)}
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
                  groupedView
                    ? "bg-blue-100 text-blue-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                Groupes
              </button>
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[11px] font-bold text-white">
                {counts.all}
              </span>
            </div>
          </div>

          <div className="mb-4 flex items-center rounded-md border border-slate-300 bg-white px-2.5 py-0.5 shadow-sm">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search"
              className="w-full border-0 bg-transparent px-2 py-1.5 text-[12px] text-slate-700 outline-none ring-0 placeholder:text-slate-400"
            />
            <button
              type="button"
              onClick={() =>
                setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))
              }
              title="Trier"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[#0d9488] transition hover:bg-slate-100"
            >
              <ArrowDownWideNarrow className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollTabs(-1)}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm hover:bg-slate-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div
              ref={tabsScrollRef}
              className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto whitespace-nowrap scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {tabs.map((tab) => {
                const active = selectedTab === tab.key;

                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setSelectedTab(tab.key)}
                    className={`inline-flex shrink-0 items-center rounded-full px-3 py-1 text-xs font-medium transition ${
                      active
                        ? `${tab.bg} ${tab.tone} shadow-sm`
                        : `${tab.bg} ${tab.tone} opacity-90 hover:opacity-100`
                    }`}
                  >
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => scrollTabs(1)}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm hover:bg-slate-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          {groupedView ? (
            <div className="space-y-2">
              {groupedVehicles.map(([groupName, groupVehicles]) => {
                const isExpanded = expandedGroups[groupName] !== false;

                return (
                  <div key={groupName} className="rounded-xl border border-slate-200 bg-white/70 p-2">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedGroups((prev) => ({
                          ...prev,
                          [groupName]: !isExpanded,
                        }))
                      }
                      className="flex w-full items-center justify-between rounded-lg px-2 py-1 text-left hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-2">
                        <ChevronDown
                          className={`h-4 w-4 text-slate-500 transition-transform ${
                            isExpanded ? "rotate-0" : "-rotate-90"
                          }`}
                        />
                        <MdOutlineDirectionsCar className="h-4 w-4 text-[#0d9488]" />
                        <span className="text-[12px] font-semibold uppercase tracking-[0.02em] text-slate-800">
                          {groupName}
                        </span>
                      </div>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                        {groupVehicles.length}
                      </span>
                    </button>

                    {isExpanded ? (
                      <div className="mt-1.5 space-y-1.5">
                        {groupVehicles.map((vehicle, index) => {
                          const fallbackKey = `${groupName}-${index}`;
                          const detailKey = getVehicleKey(vehicle, fallbackKey);
                          const detailOpen = Boolean(expandedVehicleDetails[detailKey]);

                          return (
                            <div
                              key={vehicle.id ?? vehicle.uniqueId ?? fallbackKey}
                              className={`space-y-1 rounded-2xl transition ${
                                isVehicleSelected(vehicle) ? "ring-2 ring-blue-300" : ""
                              }`}
                              onClick={() => onSelectedVehicleChange(vehicle)}
                            >
                              <VehicleItem
                                vehicle={vehicle}
                                navigate={navigate}
                                compact={lineView}
                                showDetailsToggle={lineView}
                                detailsOpen={lineView && detailOpen}
                                onToggleDetails={() => toggleVehicleDetails(vehicle, fallbackKey)}
                                checked={isVehicleSelected(vehicle)}
                                onCheckboxChange={() => handleCheckboxToggle(vehicle)}
                                showActionButtons={showVehicleActions}
                              />
                              {lineView && detailOpen ? (
                                <div className="pl-1.5">
                                  <VehicleItem
                                    vehicle={vehicle}
                                    navigate={navigate}
                                    compact={false}
                                    checked={isVehicleSelected(vehicle)}
                                    onCheckboxChange={() => handleCheckboxToggle(vehicle)}
                                    showActionButtons={showVehicleActions}
                                  />
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-1.5">
              {paginatedVehicles.map((vehicle, index) => {
                const detailKey = getVehicleKey(vehicle, index);
                const detailOpen = Boolean(expandedVehicleDetails[detailKey]);

                return (
                  <div
                    key={vehicle.id ?? vehicle.uniqueId ?? index}
                    className={`space-y-1 rounded-2xl transition ${
                      isVehicleSelected(vehicle) ? "ring-2 ring-blue-300" : ""
                    }`}
                    onClick={() => onSelectedVehicleChange(vehicle)}
                  >
                    <VehicleItem
                      vehicle={vehicle}
                      navigate={navigate}
                      compact={lineView}
                      showDetailsToggle={lineView}
                      detailsOpen={lineView && detailOpen}
                      onToggleDetails={() => toggleVehicleDetails(vehicle, index)}
                      checked={isVehicleSelected(vehicle)}
                      onCheckboxChange={() => handleCheckboxToggle(vehicle)}
                      showActionButtons={showVehicleActions}
                    />
                    {lineView && detailOpen ? (
                      <div className="pl-1.5">
                        <VehicleItem
                          vehicle={vehicle}
                          navigate={navigate}
                          compact={false}
                          checked={isVehicleSelected(vehicle)}
                          onCheckboxChange={() => handleCheckboxToggle(vehicle)}
                          showActionButtons={showVehicleActions}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-blue-100 bg-white/80 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-700 text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                {"<"}
              </button>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-700 text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                {">"}
              </button>
            </div>

            <div className="text-sm font-medium text-slate-600">
              Page <span className="text-blue-600">{currentPage}</span> / {totalPages}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleList;
